import Email from '@react-spectrum/s2/icons/Email';
import { iconStyle } from '@react-spectrum/s2/style' with { type: 'macro' };
import { useEffect, useRef, useState } from 'react';

import { useBridgewayApi } from '@/api/BridgewayApi';
import { useUserId } from '@/auth/UserIdContext';
import { BlobIframe } from '@/components/blob/BlobIframe';
import { getBlobAccessUrl } from '@/hooks/useBlobAccessUrl';

import type {
	EmailHtml,
	EmailPlaintext,
} from '../../../../bridge/types/artifact.ts';

const emailIconStyles = iconStyle({ size: 'XL', color: 'gray' });

export const tileThumbnailClass =
	'relative w-full aspect-square shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-white';

const detailThumbnailClass =
	'relative w-full overflow-hidden rounded-lg border border-gray-200 bg-white';

const IFRAME_WIDTH = 600;
const IFRAME_HEIGHT = 1800;

type EmailInstanceDoc = {
	id: string;
	html_link?: string | null;
};

interface EmailThumbnailProps {
	artifact: EmailHtml | EmailPlaintext;
	size?: 'tile' | 'detail';
}

export const EmailThumbnail = ({
	artifact,
	size = 'tile',
}: EmailThumbnailProps) => {
	const bridgewayApi = useBridgewayApi();
	const userId = useUserId();
	const cosmosId =
		artifact.type === 'email_html'
			? (artifact.content.instance?.cosmos_id ?? null)
			: null;
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const [detailHtml, setDetailHtml] = useState<string | null>(null);
	const [detailHeight, setDetailHeight] = useState<number | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const detailIframeRef = useRef<HTMLIFrameElement>(null);
	const [scale, setScale] = useState(0.5);

	useEffect(() => {
		if (!userId || !cosmosId) {
			setPreviewUrl(null);
			return;
		}
		let cancelled = false;
		bridgewayApi
			.get<EmailInstanceDoc>(`/api/user/${userId}/email/instance/${cosmosId}`)
			.then((instance) => {
				if (cancelled) return;
				setPreviewUrl(instance.html_link?.trim() || null);
			})
			.catch(() => {
				if (cancelled) return;
				setPreviewUrl(null);
			});
		return () => {
			cancelled = true;
		};
	}, [bridgewayApi, cosmosId, userId]);

	useEffect(() => {
		if (size !== 'tile' || !containerRef.current) return;
		const element = containerRef.current;
		const update = () => {
			setScale(element.clientWidth / IFRAME_WIDTH);
		};
		update();
		const ro = new ResizeObserver(update);
		ro.observe(element);
		return () => ro.disconnect();
	}, [size]);

	useEffect(() => {
		if (size !== 'detail' || !previewUrl) {
			setDetailHtml(null);
			setDetailHeight(null);
			return;
		}
		let cancelled = false;
		setDetailHtml(null);
		setDetailHeight(null);
		getBlobAccessUrl(previewUrl, userId)
			.then((accessUrl) => fetch(accessUrl))
			.then((res) => (res.ok ? res.text() : null))
			.then((text) => {
				if (cancelled || !text) return;
				setDetailHtml(text);
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, [previewUrl, size, userId]);

	const handleDetailIframeLoad = () => {
		const doc = detailIframeRef.current?.contentDocument;
		if (!doc) return;
		setDetailHeight(doc.documentElement.scrollHeight);
	};

	const containerClass =
		size === 'tile' ? tileThumbnailClass : detailThumbnailClass;

	if (previewUrl) {
		if (size === 'tile') {
			return (
				<div ref={containerRef} className={containerClass}>
					<BlobIframe
						src={previewUrl}
						sandbox="allow-same-origin"
						title="Email preview"
						tabIndex={-1}
						className="pointer-events-none absolute top-0 left-1/2 origin-top
							border-0"
						style={{
							width: IFRAME_WIDTH,
							height: IFRAME_HEIGHT,
							transform: `translateX(-50%) scale(${scale})`,
						}}
					/>
				</div>
			);
		}
		return (
			<div className={containerClass}>
				{detailHtml && (
					<iframe
						ref={detailIframeRef}
						srcDoc={detailHtml}
						title="Email preview"
						onLoad={handleDetailIframeLoad}
						scrolling="no"
						className="block w-full border-0"
						style={{ height: detailHeight ?? 600 }}
					/>
				)}
			</div>
		);
	}

	return (
		<div
			className={`${containerClass} flex items-center justify-center`}
			aria-hidden="true"
			style={size === 'detail' ? { minHeight: 200 } : undefined}
		>
			<Email styles={emailIconStyles} />
		</div>
	);
};
